import { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Check,
  ChevronDown,
  Flag,
  Inbox,
  Mail,
  Paperclip,
  SlidersHorizontal,
  Star,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import {
  INBOX_FILTER_DEFAULT,
  MAIL_COLOR_LABELS,
  inboxFilterIsActive,
  type InboxListFilter,
  type MailColorLabel,
} from "@/lib/mail/messageLabels";
import { cn } from "@/lib/cn";

type FilterOptionValue = "all" | "unread" | "important" | "favorite" | "attachments" | MailColorLabel;

type MainOption = {
  value: Exclude<FilterOptionValue, MailColorLabel>;
  label: string;
  shortLabel: string;
  icon: LucideIcon;
};

const MAIN_OPTIONS: MainOption[] = [
  { value: "all", label: "Tous les messages", shortLabel: "Tous", icon: Inbox },
  { value: "unread", label: "Non lus", shortLabel: "Non lus", icon: Mail },
  { value: "important", label: "Important", shortLabel: "Important", icon: Flag },
  { value: "favorite", label: "Favoris", shortLabel: "Favoris", icon: Star },
  { value: "attachments", label: "Avec pièce jointe", shortLabel: "PJ", icon: Paperclip },
];

const PANEL_WIDTH = 220;

function filterToOptionValue(filter: InboxListFilter): FilterOptionValue {
  if (filter.colorLabel) return filter.colorLabel;
  if (filter.unreadOnly) return "unread";
  if (filter.importantOnly) return "important";
  if (filter.favoriteOnly) return "favorite";
  if (filter.attachmentsOnly) return "attachments";
  return "all";
}

function optionValueToFilter(value: FilterOptionValue): InboxListFilter {
  if (value === "all") return INBOX_FILTER_DEFAULT;
  if (value === "unread") return { ...INBOX_FILTER_DEFAULT, unreadOnly: true };
  if (value === "important") return { ...INBOX_FILTER_DEFAULT, importantOnly: true };
  if (value === "favorite") return { ...INBOX_FILTER_DEFAULT, favoriteOnly: true };
  if (value === "attachments") return { ...INBOX_FILTER_DEFAULT, attachmentsOnly: true };
  return { ...INBOX_FILTER_DEFAULT, colorLabel: value };
}

function filterShortLabel(filter: InboxListFilter): string {
  const value = filterToOptionValue(filter);
  if (value === "all") return "Filtrer";
  const main = MAIN_OPTIONS.find((o) => o.value === value);
  if (main) return main.shortLabel;
  return MAIL_COLOR_LABELS.find((c) => c.id === value)?.label ?? "Filtrer";
}

type Props = {
  filter: InboxListFilter;
  disabled?: boolean;
  onChange: (next: InboxListFilter) => void;
};

export function InboxListFilterSelect({ filter, disabled = false, onChange }: Props) {
  const listboxId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  const active = inboxFilterIsActive(filter);
  const value = filterToOptionValue(filter);
  const label = filterShortLabel(filter);

  const updatePosition = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const margin = 6;
    const left = Math.max(8, Math.min(rect.right - PANEL_WIDTH, window.innerWidth - PANEL_WIDTH - 8));
    setCoords({ top: rect.bottom + margin, left });
  }, []);

  const close = useCallback(() => setOpen(false), []);

  const selectValue = useCallback(
    (next: FilterOptionValue) => {
      onChange(optionValueToFilter(next));
      close();
    },
    [close, onChange]
  );

  useEffect(() => {
    if (!open) return;
    updatePosition();

    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") close();
    }
    function onPointer(e: MouseEvent) {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      close();
    }
    function onResize() {
      updatePosition();
    }

    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onPointer);
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", close, true);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onPointer);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", close, true);
    };
  }, [close, open, updatePosition]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        onClick={() => {
          if (disabled) return;
          if (open) {
            close();
            return;
          }
          updatePosition();
          setOpen(true);
        }}
        className={cn(
          "inline-flex h-7 max-w-[7.5rem] shrink-0 items-center gap-1 rounded-lg border px-2 text-[11px] font-medium transition",
          "border-[var(--mail-border-soft)] bg-[var(--mail-surface)] text-[var(--mail-text)] shadow-[var(--mail-shadow-sm)]",
          "hover:border-[var(--mail-accent)]/30 hover:bg-[var(--mail-surface-hover)]",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--mail-accent)]/35",
          "disabled:cursor-not-allowed disabled:opacity-40",
          open && "border-[var(--mail-accent)]/40 bg-[var(--mail-accent-soft)]",
          active && "border-[var(--mail-accent)]/45 text-[var(--mail-accent)]"
        )}
      >
        <SlidersHorizontal
          className={cn("h-3 w-3 shrink-0", active ? "text-[var(--mail-accent)]" : "text-[var(--mail-muted)]")}
          aria-hidden
        />
        <span className="min-w-0 truncate">{label}</span>
        <ChevronDown
          className={cn(
            "h-3 w-3 shrink-0 text-[var(--mail-muted)] transition-transform duration-200",
            open && "rotate-180"
          )}
          aria-hidden
        />
      </button>

      {open
        ? createPortal(
            <div
              ref={panelRef}
              id={listboxId}
              role="listbox"
              aria-label="Filtrer les messages"
              className="fixed z-[200] overflow-hidden rounded-xl border border-[var(--mail-border-soft)] bg-[var(--mail-surface)] shadow-[var(--mail-shadow-md)]"
              style={{ top: coords.top, left: coords.left, width: PANEL_WIDTH }}
            >
              <div className="border-b border-[var(--mail-border-soft)] px-3 py-2">
                <p className="text-[11px] font-semibold text-[var(--mail-text)]">Afficher</p>
                <p className="text-[10px] text-[var(--mail-muted)]">Filtrez la liste des messages</p>
              </div>

              <div className="py-1">
                {MAIN_OPTIONS.map((option) => {
                  const Icon = option.icon;
                  const selected = value === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      onClick={() => selectValue(option.value)}
                      className={cn(
                        "flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition",
                        selected
                          ? "bg-[var(--mail-accent-soft)] text-[var(--mail-accent)]"
                          : "text-[var(--mail-text)] hover:bg-[var(--mail-surface-hover)]"
                      )}
                    >
                      <Icon
                        className={cn(
                          "h-4 w-4 shrink-0",
                          selected ? "text-[var(--mail-accent)]" : "text-[var(--mail-muted)]",
                          option.value === "important" && selected && "fill-amber-500 text-amber-500",
                          option.value === "important" && !selected && "text-amber-500/80"
                        )}
                        aria-hidden
                      />
                      <span className="min-w-0 flex-1 truncate font-medium">{option.label}</span>
                      {selected ? (
                        <Check className="h-4 w-4 shrink-0 text-[var(--mail-accent)]" strokeWidth={2.5} aria-hidden />
                      ) : (
                        <span className="h-4 w-4 shrink-0" aria-hidden />
                      )}
                    </button>
                  );
                })}
              </div>

              <div className="border-t border-[var(--mail-border-soft)] px-3 py-2.5">
                <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-[var(--mail-muted)]">
                  Couleur
                </p>
                <div className="flex flex-wrap gap-2">
                  {MAIL_COLOR_LABELS.map((color) => {
                    const selected = value === color.id;
                    return (
                      <button
                        key={color.id}
                        type="button"
                        role="option"
                        aria-selected={selected}
                        title={color.label}
                        aria-label={color.label}
                        onClick={() => selectValue(selected ? "all" : color.id)}
                        className={cn(
                          "group relative h-6 w-6 rounded-full border-2 transition",
                          selected
                            ? "border-[var(--mail-text)] ring-2 ring-[var(--mail-accent)]/35"
                            : "border-white/80 hover:scale-105"
                        )}
                        style={{ backgroundColor: color.swatch }}
                      >
                        {selected ? (
                          <Check
                            className="absolute inset-0 m-auto h-3 w-3 text-white drop-shadow-sm"
                            strokeWidth={3}
                            aria-hidden
                          />
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}
